import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { UseActiveWorkflowsResponse } from '@/pages/ReleaseManagement/hooks/useArtifactWorkflows';
import { publicApi } from '@/utils/api';
import { activePipelineablesPath } from '@/utils/monolithApiRouteService';
import { queryClient } from '@/utils/query';

const useAppWorkflowsForFilter = ({
  appSlug,
  keyword = '',
  withPipelineWorkflows,
}: {
  appSlug?: string;
  /** Narrows the listed workflows and pipelines, e.g. to a search term. */
  keyword?: string;
  withPipelineWorkflows: boolean;
}) => {
  const queryKey = useCallback(
    (keyword: string = '') => [appSlug, keyword, withPipelineWorkflows],
    [appSlug, withPipelineWorkflows],
  );
  const queryFn = useCallback(
    (keyword: string = '') =>
      async () => {
        const response = await publicApi.get<{
          data?: {
            activeWorkflows: {
              item: {
                name: string;
              };
            }[];
            activePipelines: {
              item: {
                name: string;
              };
            }[];
          };
        }>(activePipelineablesPath(appSlug || ''), {
          params: { with_pipeline_workflows: withPipelineWorkflows, pipelineable: keyword },
        });

        return {
          workflows: response?.data?.data?.activeWorkflows?.map((item: any) => item.name as string) ?? [],
          pipelines: response?.data?.data?.activePipelines?.map((item: any) => item.name as string) ?? [],
        };
      },

    [appSlug, withPipelineWorkflows],
  );

  const { data, isLoading } = useQuery<unknown, unknown, UseActiveWorkflowsResponse>({
    queryKey: queryKey(keyword),
    queryFn: queryFn(keyword),
    retry: false,
  });

  const fetchQuery = useCallback(
    (keyword: string) => {
      return queryClient.fetchQuery({
        queryKey: queryKey(keyword),
        queryFn: queryFn(keyword),
        retry: false,
      });
    },
    [queryFn, queryKey],
  );

  return {
    data,
    fetchQuery,
    isLoading,
  };
};

export default useAppWorkflowsForFilter;
